/** SUNTRONIX ONE — Unified Approval Inbox (/work/approvals).
 *  One queue over existing approval frameworks — no new engine. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, X, ExternalLink, HelpCircle, Loader2, StampIcon } from "lucide-react";

export default function UnifiedApprovals() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [infoTarget, setInfoTarget] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/collab/approvals-inbox")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Could not load approvals"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (item, action) => {
    setBusy(item.key + action.label);
    try {
      await api.request({ method: action.api.method, url: action.api.url, data: action.api.body });
      toast.success(`${action.label}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 py-5" data-testid="unified-approvals-page">
      <div className="flex items-center gap-2 mb-4">
        <StampIcon className="h-4 w-4 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-900">Approvals</h1>
        {items && <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{items.length}</span>}
        <span className="text-[11px] text-slate-400 ml-auto">One queue · all approval frameworks</span>
      </div>

      {!items ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md px-4 py-8 text-center">
          Nothing waiting for approval. 🎉
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => (
            <div key={it.key} className="rounded-md border border-slate-200 bg-white px-4 py-3"
              data-testid={`approval-card-${it.key}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{it.title}</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">
                    {it.requester && <>Requested by <span className="text-slate-700">{it.requester}</span></>}
                    {it.record && <> · <span className="font-mono">{it.record}</span></>}
                    {it.at && <> · {(it.at || "").slice(0, 16).replace("T", " ")}</>}
                  </div>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">{it.source.replace(/_/g, " ")}</span>
              </div>
              {it.reason && <div className="text-[12px] text-slate-600 mt-1.5">{it.reason}</div>}
              {(it.current_value != null || it.requested_value != null) && (
                <div className="text-[12px] text-slate-700 mt-1.5">
                  <span className="line-through text-slate-400">{String(it.current_value ?? "—")}</span>
                  <span className="mx-1.5">→</span>
                  <span className="font-medium">{String(it.requested_value ?? "—")}</span>
                </div>
              )}
              {it.impact && <div className="text-[11px] text-slate-400 mt-0.5">Impact: {it.impact}</div>}
              <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                {(it.actions || []).map((a) => (
                  <button key={a.label} disabled={busy === it.key + a.label}
                    onClick={() => act(it, a)}
                    data-testid={`approval-${a.style}-${it.key}`}
                    className={`h-8 px-3 rounded text-[12px] font-medium inline-flex items-center gap-1 disabled:opacity-50 ${a.style === "approve"
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "bg-white border border-rose-200 text-rose-700 hover:bg-rose-50"}`}>
                    {a.style === "approve" ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                    {a.label}
                  </button>
                ))}
                <button onClick={() => setInfoTarget(it)}
                  data-testid={`approval-info-${it.key}`}
                  className="h-8 px-3 rounded text-[12px] border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1">
                  <HelpCircle className="h-3.5 w-3.5" /> Request info
                </button>
                {it.route && (
                  <button onClick={() => navigate(it.route)}
                    data-testid={`approval-open-${it.key}`}
                    className="h-8 px-3 rounded text-[12px] border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1 ml-auto">
                    <ExternalLink className="h-3.5 w-3.5" /> Open record
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <RequestInfoDialog target={infoTarget} onClose={() => setInfoTarget(null)} />
    </div>
  );
}

function RequestInfoDialog({ target, onClose }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMsg(""); }, [target]);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/collab/approvals-inbox/request-info", {
        key: target.key, title: target.title, requester: target.requester,
        message: msg, route: target.route,
      });
      toast.success("Information requested");
      onClose();
    } catch { toast.error("Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="request-info-dialog">
        <DialogHeader><DialogTitle className="text-base">Request information</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-500">{target?.title} — {target?.requester}</div>
        <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)}
          placeholder="What do you need from the requester?"
          data-testid="request-info-message"
          className="w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-slate-400" />
        <button onClick={send} disabled={busy || !msg.trim()}
          data-testid="request-info-send"
          className="h-9 rounded-md bg-slate-900 text-white text-[13px] font-medium disabled:opacity-40 hover:bg-slate-800">
          Send request
        </button>
      </DialogContent>
    </Dialog>
  );
}
